import { debounce } from 'lodash';
import { Input, message } from 'antd';
import { makeStyles } from '@material-ui/styles';
import { useLazyQuery, gql } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import React, { useState, useContext, useEffect } from 'react';
import TipsWrap from 'common/components/tips-wrap';
import { checkPropertyNameInCity } from '~pages/onboarding/queries';
import event from '~helpers/mixpanel';
import hexToRGBA from '~helpers/hex-to-rgba';
import OnboardingContext from '../context';

const { TextArea } = Input;

const PROPERTY_NAME_MAX_LENGTH = 250;
const PROPERTY_NAME_ALREADY_EXISTS_IN_CITY = 'PROPERTY_NAME_ALREADY_EXISTS_IN_CITY';

const emojiRegex = new RegExp(
  /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g,
);

const useStyles = makeStyles(theme => ({
  wrap: {
    ...theme.flex.colCross,
    padding: '136px 0',
    position: 'relative',

    '& h2': {
      ...theme.typography.h2,
      margin: '0 0 48px 0',
    },
  },
  main: {
    width: '565px',
    margin: '0 auto',
  },
  label: {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 600,
    marginBottom: '24px',
  },
  textAreaWrap: {
    position: 'relative',
  },
  textArea: {
    height: '100px !important',
    padding: '20px',
    border: 'none',
    backgroundColor: theme.colors.gray1,
    boxShadow: '0 12px 32px 2 rgba(107, 146, 243, 0.19)',
    borderRadius: '2px',
    resize: 'none',
    color: `${hexToRGBA(theme.colors.gray10, 0.65)}`,

    '&::placeholder': {
      fontSize: '14px',
      lineHeight: '22px',
      color: `${hexToRGBA(theme.colors.gray10, 0.45)} !important`,
    },
  },
  errorTextArea: {
    height: '100px !important',
    padding: '20px',
    border: `1px solid ${theme.colors.red7}`,
    backgroundColor: theme.colors.gray1,
    boxShadow: '0 12px 32px 2 rgba(107, 146, 243, 0.19)',
    borderRadius: '2px',
    resize: 'none',
  },
  count: {
    position: 'absolute',
    right: '20px',
    bottom: '10px',
    fontSize: '12px',
    lineHeight: '20px',
    color: hexToRGBA(theme.colors.gray10, 0.45),

    '& > span': {
      color: hexToRGBA(theme.colors.gray10, 0.65),
    },
  },
  errorMessage: {
    fontSize: '12px',
    lineHeight: '20px',
    color: theme.colors.red6,
    marginBottom: 24,
  },
  errorMessageMarginTop: {
    marginTop: 12,
  },
}));

const Name = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  useEffect(() => {
    event('Viewed OP Create Name Page');
  }, []);

  const { setPropertyName, propertyName, mapAddressData, propertyTypeSlug } =
    useContext(OnboardingContext);
  const [propertyNameField, setPropertyNameField] = useState({
    value: propertyName,
    isValid: null,
    errorMessageKey: '',
    nameCount: propertyName?.length ?? 0,
  });

  const [handleCheckPropertyNameInCity] = useLazyQuery(gql(checkPropertyNameInCity()?.query), {
    onCompleted: ({ checkPropertyNameInCity }) => {
      if (checkPropertyNameInCity?.success) {
        setPropertyNameField({
          ...propertyNameField,
          isValid: true,
          errorMessageKey: '',
        });

        setPropertyName(propertyNameField?.value?.trim());
      }
    },
    onError: ({ graphQLErrors }) => {
      if (graphQLErrors?.[0]?.message === PROPERTY_NAME_ALREADY_EXISTS_IN_CITY) {
        setPropertyNameField({
          ...propertyNameField,
          isValid: false,
          errorMessageKey: 'landlord.onboarding.property_name.error_message.duplicate',
        });
      } else {
        message.error(t('sorry_an_error_occurred'));
      }
    },
    fetchPolicy: 'no-cache',
  });

  const handleChangePropertyName = e => {
    const propertyName = e?.target?.value;
    if (propertyName) {
      setPropertyName();
    }

    if (propertyName?.length < 2) {
      setPropertyNameField({
        value: undefined,
        isValid: false,
        nameCount: propertyName?.length,
        errorMessageKey: 'landlord.onboarding.property_name.error_message.min_characters',
      });
      return;
    }

    if (propertyName && emojiRegex.test(propertyName)) {
      setPropertyNameField({
        value: undefined,
        isValid: false,
        nameCount: propertyName?.length,
        errorMessageKey: 'landlord.onboarding.property_name.error_message.format',
      });

      return;
    }

    setPropertyNameField({
      isValid: true,
      errorMessageKey: '',
      value: propertyName,
      nameCount: propertyName?.length,
    });

    handleCheckPropertyNameInCity({
      variables: {
        input: {
          cityId: mapAddressData?.cityId,
          propertyName: propertyName?.trim(),
        },
      },
    });
  };

  const tipTextList = [
    t('landlord.onboarding.property_name.tips1'),
    t('landlord.onboarding.property_name.tips2'),
    t('landlord.onboarding.property_name.tips3'),
  ];

  return (
    <div className={classes.wrap}>
      <h2>{t('landlord.onboarding.property_name.title')}</h2>
      <div className={classes.main}>
        <div className={classes.label}>{t('landlord.onboarding.property_name.label')}</div>
        <div className={classes.textAreaWrap}>
          <TextArea
            onClick={() => {
              event('Clicked OP Create Name Page', {
                'Clicked Position': 'Name Blank',
                'Property Type': propertyTypeSlug,
              });
            }}
            maxLength={PROPERTY_NAME_MAX_LENGTH}
            className={
              propertyNameField?.errorMessageKey ? classes.errorTextArea : classes.textArea
            }
            defaultValue={propertyNameField?.value}
            placeholder={t('landlord.onboarding.property_name.placeholder')}
            onChange={debounce(handleChangePropertyName, 300)}
          />
          <div className={classes.count}>
            <span>{`${propertyNameField?.nameCount}`}</span>
            {`/${PROPERTY_NAME_MAX_LENGTH}`}
          </div>
        </div>
        <div
          className={classNames(classes.errorMessage, {
            [classes.errorMessageMarginTop]: propertyNameField?.errorMessageKey,
          })}
        >
          <If condition={propertyNameField?.errorMessageKey}>
            {t(propertyNameField?.errorMessageKey)}
          </If>
        </div>
        <TipsWrap tipTextList={tipTextList} />
      </div>
    </div>
  );
};

export default Name;
